import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Card, Select, DatePicker, Row, Col, Space, message } from "antd";
import { selectCurrentUser } from "../../../auth/store/authSlice";
import { PlusOutlined, DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import FacturasTable from "../../../admin/facturas/components/FacturasTable";
import PayAllInvoicesButton from "../../../../components/PayAllInvoicesButton/PayAllInvoicesButton";
import clientBillingApi from "../store/api";
import {
  readFacturaListState,
  writeFacturaListState,
  getFacturaIdSortValue
} from "../../../admin/facturas/helper";
import { shortId } from "helper/util";
import "../../../admin/facturas/components/FacturasList.scss";

const LIST_STATE_KEY = "client-facturas";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ClientFacturasList = () => {
  const history = useHistory();
  const location = useLocation();
  const [flashingFacturaId, setFlashingFacturaId] = useState(null);

  const [facturasData, setFacturasData] = useState([]);
  const [loading, setLoading] = useState(false);
  const currentUser = useSelector(selectCurrentUser);

  // Restore whatever the user last had here, so returning from an invoice keeps
  // their filters, page and sorting. No default date range - show all invoices.
  const initialState = React.useMemo(
    () =>
      readFacturaListState(LIST_STATE_KEY, {
        filters: {
          dateRange: null,
          status: ["unpaid", "partial", "overdue"],
          facturaType: []
        },
        currentPage: 1,
        sortConfig: { key: "facturaId", direction: "descending" }
      }),
    []
  );

  const [filters, setFilters] = useState(initialState.filters);
  const [sortConfig, setSortConfig] = useState(initialState.sortConfig);
  // Frontend pagination - handle page changes without backend calls
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const pageSize = 10;

  const loadFacturas = React.useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        pageSize: 1000 // Load all data to enable proper sorting across all records
      };

      // Add date range filter
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.startDate = filters.dateRange[0].format("YYYY-MM-DD");
        params.endDate = filters.dateRange[1].format("YYYY-MM-DD");
      }

      // Add status filter
      if (filters.status && filters.status.length > 0) {
        params.status = filters.status.join(",");
      }

      // Add facturaType filter
      if (filters.facturaType && filters.facturaType.length > 0) {
        params.facturaType = filters.facturaType.join(",");
      }

      // Client filtering is now handled server-side

      const response = await clientBillingApi.getFacturas(params);
      setFacturasData(response.data || []);
    } catch (error) {
      console.error("Error loading facturas:", error);
      message.error("Failed to load facturas");
    } finally {
      setLoading(false);
    }
  }, [filters.dateRange, filters.status, filters.facturaType, currentUser]);

  useEffect(() => {
    // Load initial facturas data with default filters
    loadFacturas();
  }, [loadFacturas]);

  // Reload facturas when filters change
  useEffect(() => {
    loadFacturas();
  }, [loadFacturas]);

  useEffect(() => {
    // Check if we're returning from create with a newly created factura
    const searchParams = new URLSearchParams(location.search);
    const newFacturaId = searchParams.get("created");

    if (newFacturaId) {
      // Set flashing animation
      setFlashingFacturaId(newFacturaId);

      // Remove the flash after 3 seconds
      setTimeout(() => {
        setFlashingFacturaId(null);
      }, 3000);

      // Clean up URL
      history.replace("/billing/facturas");
    }
  }, [location.search, history]);

  // Keep the list state around so opening an invoice and coming back doesn't wipe it
  useEffect(() => {
    writeFacturaListState(LIST_STATE_KEY, { filters, currentPage, sortConfig });
  }, [filters, currentPage, sortConfig]);

  const handleCreateFactura = () => {
    history.push("/billing/facturas/create");
  };

  const handleViewFactura = (facturaId) => {
    // Find the factura from current data and pass it via state
    const factura = facturasData.find((f) => f.id === facturaId);
    history.push(`/billing/facturas/${facturaId}`, { factura });
  };

  const handleRefresh = () => {
    loadFacturas();
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value
    }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Get paginated data from sorted data
  const getPaginatedData = () => {
    const sortedData = getSortedData();
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  };

  // Sorting functions
  const getSortedData = () => {
    if (!sortConfig.key) return facturasData;

    const sortedData = [...facturasData].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle date values
      if (sortConfig.key === "facturaDate") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle numeric values
      if (sortConfig.key === "total" || sortConfig.key === "totalPaid") {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }

      // Handle status (custom order: unpaid, partial, paid, overdue, cancelled)
      if (sortConfig.key === "status") {
        const statusOrder = { unpaid: 0, partial: 1, paid: 2, overdue: 3, cancelled: 4 };
        aVal = statusOrder[aVal] || 0;
        bVal = statusOrder[bVal] || 0;
      }

      // Handle facturaType (alphabetical order)
      if (sortConfig.key === "facturaType") {
        aVal = aVal || "Manual";
        bVal = bVal || "Manual";
      }

      // Handle facturaId (sort by date first, then by duplicate suffix)
      if (sortConfig.key === "facturaId") {
        aVal = getFacturaIdSortValue(aVal);
        bVal = getFacturaIdSortValue(bVal);
      }

      if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });

    return sortedData;
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const handleExportFacturaPdf = async (facturaId) => {
    try {
      const response = await clientBillingApi.exportFacturaPdf(facturaId);
      const blob = new Blob([response], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.target = "_blank";
      const factura = facturasData.find((f) => f.id === facturaId);
      const facturaIdShort = factura?.facturaId || shortId(facturaId);
      link.download = `factura-${facturaIdShort}-${new Date().getTime()}.pdf`;
      window.open(link.href, "_blank");
    } catch (err) {
      console.log(err);
      message.error("Failed to export PDF");
    }
  };

  const exportToCSV = () => {
    try {
      const dataToExport = getSortedData();

      if (dataToExport.length === 0) {
        message.warning("No data to export");
        return;
      }

      // Define CSV headers (no Client column for client side)
      const headers = ["Date", "Factura ID", "Status", "Total", "Paid", "Type"];

      // Convert data to CSV format
      const csvContent = [
        headers.join(","),
        ...dataToExport.map((row) =>
          [
            new Date(row.facturaDate).toISOString().split("T")[0],
            `"${row.facturaId}"`,
            row.status,
            row.total || 0,
            row.totalPaid || 0,
            row.facturaType || "Manual"
          ].join(",")
        )
      ].join("\n");

      // Create and download the file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `facturas-export-${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success("CSV exported successfully");
    } catch (error) {
      console.error("Export failed:", error);
      message.error("Failed to export CSV");
    }
  };

  return (
    <div className="facturas-list">
      <div className="facturas-header">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={24}>
            <Card size="small">
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={16} md={10}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Date Range</div>
                  <RangePicker
                    value={filters.dateRange}
                    onChange={(dates) => handleFilterChange("dateRange", dates)}
                    format="YYYY-MM-DD"
                    style={{ width: "100%" }}
                  />
                </Col>

                <Col xs={24} sm={6} md={7}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Status</div>
                  <Select
                    mode="multiple"
                    placeholder="All statuses"
                    value={filters.status}
                    onChange={(value) => handleFilterChange("status", value)}
                    allowClear
                    style={{ width: "100%" }}
                  >
                    <Option value="unpaid">Unpaid</Option>
                    <Option value="partial">Partial</Option>
                    <Option value="paid">Paid</Option>
                    <Option value="overdue">Overdue</Option>
                    <Option value="cancelled">Cancelled</Option>
                  </Select>
                </Col>

                <Col xs={24} sm={6} md={7}>
                  <div style={{ marginBottom: 8, fontWeight: 500 }}>Type</div>
                  <Select
                    mode="multiple"
                    placeholder="All types"
                    value={filters.facturaType}
                    onChange={(value) => handleFilterChange("facturaType", value)}
                    allowClear
                    style={{ width: "100%" }}
                  >
                    <Option value="Managed">Managed</Option>
                    <Option value="SaaS">SaaS</Option>
                    <Option value="Manual">Manual</Option>
                  </Select>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col
            xs={24}
            lg={6}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start"
            }}
          >
            <Space>
              <PayAllInvoicesButton
                facturas={getSortedData()}
                onSuccess={handleRefresh}
                size="small"
                isClientSide={true}
              />
              <Button size="small" icon={<DownloadOutlined />} onClick={exportToCSV}>
                Export CSV
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleCreateFactura}
                style={{ display: "none" }} // Hide create button for clients
              >
                Create Factura
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="content-table">
        <FacturasTable
          dataSource={getPaginatedData()}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: facturasData.length,
            size: "small",
            showSizeChanger: false,
            onChange: handlePageChange
          }}
          onRowClick={handleViewFactura}
          flashingFacturaId={flashingFacturaId}
          loading={loading}
          onRefresh={handleRefresh}
          sortConfig={sortConfig}
          onSort={handleSort}
          readOnly={true}
          isClientSide={true}
          onViewPDF={handleExportFacturaPdf}
        />
      </div>
    </div>
  );
};

export default ClientFacturasList;
